/**
 * @author Mr_FabiozZz[mr.fabiozzz@gmail.com]
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import sass from "pages/StepPages/StepFour/Two/Calculate/calculate.module.sass";
import cn from "classnames";
import InputTemp from "components-new/InputTemp/index";
import HeadPage from "pages/StepPages/StepFour/HeadPage";
import { Button } from "components/shared";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import { useAppSelector } from "toolkit/hooks";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
	generateIndex,
	getTopChord,
	makeString,
	names,
	prettify,
} from "pages/StepPages/StepFour/Two/Calculate/helpers";
import { Row } from "pages/StepPages/StepFour/Two/Calculate/Row";
import { Anchor } from "pages/StepPages/StepFour/Two/Calculate/Anchor";
import { organizationAPI } from "libs/apiClient/Organization";
import { directoryAPI } from "libs/apiClient/Directory";
import {
	EconomicalDamageResponse,
	ObjectLocationInfoResponse,
	OrganizationObjectThreatResponse,
} from "libs/apiClient/data-contracts";

export interface IFormulasState {
	name: any;
	description: string;
	type: string;
	fields: {
		label: string;
		footer: string;
		value: string;
	}[];
	result: string;
}

const Calculate = () => {
	/**
	 * Ниже идет инициализация ссылок на каждую формулу, а так же на сам контейнер этих формул
	 */
	const containerRef = useRef<any>(null);
	const INDEX_EIGHT = useRef<any>(null);
	const INDEX_NINE = useRef<any>(null);
	const K_NP = useRef<any>(null);
	const D_NDPI = useRef<any>(null);
	const K_NDPI = useRef<any>(null);
	const D_DIV = useRef<any>(null);
	const K_NS = useRef<any>(null);
	const D_VSO = useRef<any>(null);
	const B_MIDDLE = useRef<any>(null);
	const INDEX_TEN = useRef<any>(null);

	/**
	 * Инициальное значение для формул перенесена в компонент для того,
	 * чтобы можно было положить в каждую формулу соответствующую ссылку
	 */
	const initialState: Array<IFormulasState & { addIcon: boolean; label: boolean; ref: any; removeField: boolean }> = [
		{
			name: "Показатель №8",
			description: "Показатель ущерба субъекту КИИ ",
			ref: INDEX_EIGHT,
			type: "INDEX_EIGHT",
			fields: [
				{
					label: "ΔДоходов",
					footer:
						"снижение уровня доходов субъекта КИИ (с учетом налога на добавленную стоимость, акцизов и иных обязательных платежей), исчисляется в рублях",
					value: "",
				},
				{
					label: "Д (усредн)",
					footer: "объем доходов субъекта КИИ, усредненный за прошедший пятилетний период, исчисляется в рублях",
					value: "",
				},
			],
			addIcon: false,
			label: false,
			removeField: false,
			result: "0",
		},
		{
			name: "Показатель №9",
			description: "Показатель ущерба бюджетам РФ",
			ref: INDEX_NINE,
			type: "INDEX_NINE",
			fields: [
				{
					label: "ΔДБ (снижение выплат (отчислений) в бюджеты РФ)",
					value: "",
					footer:
						"снижение выплат (отчислений) в бюджеты РФ, осуществляемых субъектом КИИ, которое может быть следствием прекращения или нарушения критических процессов, исчисляется в рублях",
				},
				{
					label: "Б усредн (объем прогнозируемого годового дохода федерального бюджета Российской Федерации)",
					value: "",
					footer:
						"объем прогнозируемого годового дохода федерального бюджета РФ, усредненный за планируемый трехлетний период, исчисляется в рублях",
				},
			],
			addIcon: false,
			label: false,
			removeField: false,
			result: "0",
		},
		{
			name: (
				<span style={{ fontWeight: "inherit" }}>
					K<sub style={{ fontWeight: "inherit" }}>нп</sub>
				</span>
			),
			ref: K_NP,
			description:
				"Коэффициент выплат (отчислений) от налога на прибыль субъекта КИИ в бюджеты Российской Федерации, осуществленных за прошедший год",
			type: "K_NP",
			fields: [
				{
					label: "Налог (нп)",
					value: "",
					footer:
						"объем выплат (отчислений) субъекта КИИ в бюджеты РФ в виде налога на прибыль, осуществленных за прошедший год, исчисляется в рублях",
				},
				{
					label: "П (год)",
					value: "",
					footer: "прибыль субъекта КИИ за прошедший год, исчисляется в рублях",
				},
			],
			addIcon: false,
			label: false,
			removeField: false,
			result: "0",
		},
		{
			name: "ΔНДПИ",
			ref: D_NDPI,
			description:
				"Снижение выплат (отчислений) от налога на добычу полезных ископаемых субъекта КИИ в бюджеты Российской Федерации, рассчитывается по формуле",
			type: "D_NDPI",
			fields: [
				{
					label: "ΔКДПИ",
					value: "",
					footer: "снижение количества, добываемого полезного ископаемого, исчисляется в единицах массы или объема",
				},
				{
					label: "К (ндпи)",
					value: "",
					footer:
						"коэффициент выплат (отчислений) от налога на добычу полезных ископаемых субъекта КИИ в бюджеты РФ, осуществленных за прошедший год, исчисляется в рублях на единицу массы или объема",
				},
			],
			addIcon: false,
			label: false,
			removeField: false,
			result: "0",
		},
		{
			name: (
				<span style={{ fontWeight: "inherit" }}>
					K<sub style={{ fontWeight: "inherit" }}>ндпи</sub>
				</span>
			),
			ref: K_NDPI,
			description:
				"Коэффициент выплат (отчислений) от налога на добычу полезных ископаемых субъекта КИИ в бюджеты РФ, осуществленных за прошедший год, исчисляется в рублях на единицу массы или объема",
			type: "K_NDPI",
			fields: [
				{
					label: "Налог (ндпи)",
					value: "",
					footer:
						"объем выплат (отчислений) субъекта КИИ в бюджеты РФ в виде налога на добычу полезных ископаемых, осуществленных за прошедший год, исчисляется в рублях",
				},
				{
					label: "КДПИ (год)",
					value: "",
					footer:
						"количество добытого полезного ископаемого субъектом КИИ за прошедший год, исчисляется в единицах массы или объема",
				},
			],
			addIcon: false,
			label: false,
			removeField: false,
			result: "0",
		},
		{
			name: "ΔДИВ",
			description: "Снижение выплат (отчислений) от дивидендов по акциям субъекта КИИ в бюджеты РФ",
			ref: D_DIV,
			type: "D_DIV",
			fields: [
				{
					label: "ΔДИВ (гос)",
					value: "",
					footer:
						" снижение выплат (отчислений) от дивидендов, осуществляемых субъектом КИИ по акциям, находящимся в собственности РФ или субъекта Российской Федерации, исчисляется в рублях",
				},
				{
					label: "ΔДИВ (негос)",
					value: "",
					footer:
						"снижение выплат (отчислений) от налога на дивиденды, осуществляемых субъектом КИИ по акциям, находящимся в собственности физических или юридических лиц, исчисляется в рублях",
				},
			],
			addIcon: false,
			label: false,
			removeField: false,
			result: "0",
		},
		{
			name: (
				<span style={{ fontWeight: "inherit" }}>
					K<sub style={{ fontWeight: "inherit" }}>нс</sub>
				</span>
			),
			description:
				"Коэффициент, показывающий усредненную ставку налоговых выплат (отчислений) от дивидендов, произведенных субъектом КИИ за прошедший год",
			ref: K_NS,
			type: "K_NS",
			fields: [
				{
					label: "Н (дивиденды)",
					value: "",
					footer:
						"объем налоговых выплат (отчислений) от дивидендов, осуществленных субъектом КИИ за прошедший год, исчисляется в рублях",
				},
				{
					label: "Дивиденды",
					value: "",
					footer:
						"объем выплат (отчислений) дивидендов, осуществленных субъектом КИИ за прошедший год, исчисляется в рублях",
				},
			],
			addIcon: false,
			label: false,
			removeField: false,
			result: "0",
		},
		{
			name: "ΔВСО",
			description:
				"Снижение выплат (отчислений) сторонних организаций в бюджеты РФ, возникающее вследствие прекращения или нарушения функционирования рассматриваемого объекта КИИ",
			ref: D_VSO,
			type: "D_VSO",
			fields: [
				{
					label: "ΔВСО 1",
					value: "",
					footer:
						"снижение выплат (отчислений) i-й сторонней организации в бюджеты РФ, возникающее вследствие прекращения или нарушения функционирования рассматриваемого объекта КИИ, исчисляется в рублях",
				},
				{
					label: "ΔВСО 2",
					value: "",
					footer: "",
				},
			],
			addIcon: true,
			label: true,
			removeField: true,
			result: "0",
		},
		{
			name: (
				<span style={{ fontWeight: "inherit" }}>
					Б<sub style={{ fontWeight: "inherit" }}>усредн</sub>
				</span>
			),
			description:
				"Объем прогнозируемого годового дохода федерального бюджета РФ, усредненного за планируемый трехлетний период",
			ref: B_MIDDLE,
			type: "B_MIDDLE",
			fields: [
				{
					label: "Б (усредн) 1",
					value: "",
					footer: "прогнозируемый общий объем дохода федерального бюджета РФ за i-й год, исчисляется в рублях",
				},
				{
					label: "Б (усредн) 2",
					value: "",
					footer: "",
				},
				{
					label: "Б (усредн) 3",
					value: "",
					footer: "",
				},
			],
			addIcon: false,
			label: false,
			removeField: false,
			result: "0",
		},
		{
			name: "Показатель №10",
			description: "Ущерб от прекращения финансовых операций",
			ref: INDEX_TEN,
			type: "INDEX_TEN",
			fields: [
				{
					label: "N (операций)",
					value: "",
					footer:
						"количество операций, проведенных клиентами по банковским счетам и (или) без открытия банковского счета за прошедший год, или операций, осуществленных субъектом КИИ, являющимся в соответствии с законодательством Российской Федерации системно значимой кредитной организацией, оператором услуг платежной инфраструктуры системно и (или) социально значимых платежных систем или системно значимой инфраструктурной организацией финансового рынка, за прошедший год (или прогнозные значения количества операций для создаваемых объектов КИИ), исчисляется в млн. единиц\n",
				},
				{
					label: "N (дней)",
					value: "",
					footer: "количество календарных дней в прошедшем году",
				},
			],
			addIcon: false,
			label: false,
			removeField: false,
			result: "0",
		},
	];
	/**
	 * Индикатор загрузки формул
	 */
	const [loading, setLoading] = useState(false);
	/**
	 * Состояние для переключения локаций и получения ее id
	 */
	const [location, setLocation] = useState<{ address: string; id: number }>({ address: "", id: 0 });

	/**
	 * состояние для хранения списка адресов для выпадающего списка
	 */
	const [locationOptions, setLocationOptions] = useState<ObjectLocationInfoResponse[]>([]);

	/**
	 * Состояние для показа результирующего списка по всем адресам
	 */
	const [finalList, setFinalList] = useState<EconomicalDamageResponse[]>([]);

	/**
	 * Состояние для формул
	 */
	const [state, setState] =
		useState<Array<IFormulasState & { addIcon: boolean; label: boolean; removeField: boolean }>>(initialState);

	/**
	 * Состояние для хранения ссылок навигации по формулам
	 */
	const [object, setObject] = useState<Array<{ [key: string]: any }>>([]);

	/**
	 * Состояние для понимания в какую сторону крутит колесо мыши пользователь
	 */
	const [scrollTop, setScrollTop] = useState(false);

	/**
	 * Состояние для хранения активного объекта для которого производятся расчеты
	 */
	const [currentThread, setCurrentThread] = useState<OrganizationObjectThreatResponse | undefined>();

	/**
	 * Флаг для запрета вызова дважды
	 */
	const firstLoad = useRef(false);

	const navigate = useNavigate();

	const params = useParams();

	/**
	 * При смене параметров URL перезапрашивается объект для которого ведутся расчеты
	 */
	useEffect(() => {
		organizationAPI.objectThreatDetail(+params.threadId!).then((r) => setCurrentThread(r.data));
	}, [params.threadId]);

	/**
	 * Эффект при определенных условиях делает запросы за актуальными адресами активного объекта,
	 * установка локации из параметров URL
	 * делается запрос за формулами актуального адреса и актуального объекта для которого ведутся расчеты
	 */
	useEffect(() => {
		if (params.threadId) {
			directoryAPI.objectLocationsDetail(+params.threadId!).then((r) => {
				if (currentThread && currentThread.damageExists) {
					setLocationOptions([{ id: 0, address: "Суммарный результат по адресам" }, ...r.data]);
				} else {
					setLocationOptions(r.data);
				}
			});
		}
		if (params.addressId) {
			const location = locationOptions.find((_) => _.id === Number(params.addressId));
			location && location.id !== 0 && setLocation(location);
		}
		if (params.addressId && params.threadId) {
			organizationAPI.objectDamageDetail(+params.addressId!).then((r) => {
				let initial: typeof state = [...state];
				firstLoad.current = false;
				if (r.data.length) {
					r.data.forEach((data) => {
						const itemState = initial.find((_) => _.type === data.type);
						if (itemState) {
							if (data.values && data.values.length) {
								if (itemState.type === "D_VSO" || itemState.type === "B_MIDDLE") {
									const label = itemState.fields[0].label.replace(/\s\d+$/, "");
									let newFields: any[] = data.values.map((value: number, index: number) => ({
										label: `${label} ${index + 1}`,
										value: makeString(value),
									}));
									initial = initial.map((_) =>
										_.type === data.type
											? {
													..._,
													fields: newFields,
													result: data.result === null ? makeString(0) : makeString(data.result!),
											  }
											: _,
									);
									// setState(prevState=>prevState.map(_=>_.type === data.type? {..._, fields: newFields,result:data.result === null?makeString(0):makeString(data.result)}:_));
								} else {
									let newFields: any[] = [];
									newFields[0] = { ...itemState.fields[0], value: makeString(data.values[0] ? data.values[0] : 0) };
									newFields[1] = { ...itemState.fields[1], value: makeString(data.values[1] ? data.values[0] : 0) };
									initial = initial.map((_) =>
										_.type === data.type
											? {
													..._,
													fields: newFields,
													result: data.result === null ? makeString(0) : makeString(data.result!),
											  }
											: _,
									);
									// setState(prevState=>prevState.map(_=>_.type === data.type? {..._, fields: newFields,result:data.result === null?makeString(0):makeString(data.result)}:_));
								}
							}
						}
					});
					setState(initial);
				} else {
					setState(initialState);
				}
			});
		} else {
			firstLoad.current = true;
		}
		return () => setState(initialState);
		//eslint-disable-next-line
	}, [params, currentThread, currentThread?.damageExists]);

	/**
	 * Функция для завершения расчетов
	 */
	const endCalculate = () => {
		organizationAPI.objectSummaryCreate(+params.threadId!).then(() => {
			organizationAPI.objectThreatDetail(+params.threadId!).then((r) => setCurrentThread(r.data));
			setLocation({ id: 0, address: "Суммарный результат по адресам" });
			navigate(appLocation.pathname.replace(/address\/\d+$/, `result`), { replace: true });
		});
	};

	/**
	 * Имя организации выбранного объекта
	 */
	const name = useAppSelector((store) => store.organizations.selectedOrganization?.fullName);

	const appLocation = useLocation();

	/**
	 * Флаг для определения результирующей страницы
	 */
	const isFinal = useMemo(() => {
		return /result$/.test(appLocation.pathname);
	}, [appLocation]);

	const issetFormulas = useMemo(() => {
		for (let i = 0; i < state.length; i++) {
			if (state[i].result) {
				return true;
			}
		}
		return false;
	}, [state]);

	/**
	 * Если страница финальная делается запрос за результатами расчетов
	 */
	useEffect(() => {
		if (isFinal) {
			setLoading(true);
			organizationAPI.objectDamageDetail(+params.threadId!).then((r) => {
				setFinalList(r.data);
				setLoading(false);
			});
		}
		//eslint-disable-next-line
	}, [isFinal]);

	/**
	 * Функция прослушиватель для полей формул
	 */
	const changeItemState = (data: any) => {
		let itemState = state.find((_) => _.type === data.type);
		if (itemState) {
			if (itemState.type === "D_VSO" || itemState.type === "B_MIDDLE") {
				const label = itemState.fields[0].label.replace(/\s\d+$/, "");
				let newFields: any[] = data.values.map((value: number, index: number) => ({
					label: `${label} ${index + 1}`,
					value: makeString(value),
				}));
				setState((prevState) =>
					prevState.map((_) =>
						_.type === data.type
							? { ..._, fields: newFields, result: data.result === null ? makeString(0) : makeString(data.result) }
							: _,
					),
				);
			} else {
				let newFields: any[] = [];
				newFields[0] = { ...itemState.fields[0], value: makeString(data.values[0]) };
				newFields[1] = { ...itemState.fields[1], value: makeString(data.values[1]) };
				setState((prevState) =>
					prevState.map((_) =>
						_.type === data.type
							? { ..._, fields: newFields, result: data.result === null ? makeString(0) : makeString(data.result) }
							: _,
					),
				);
			}
		}
	};

	/**
	 * В эффекте устанавливается прослушиватель скролла для навигационных ссылок
	 */
	useEffect(() => {
		function scroll() {
			let containerTop: number;
			if (containerRef.current) {
				containerTop = containerRef.current?.getBoundingClientRect().top as number;
				if (
					INDEX_EIGHT.current &&
					INDEX_NINE.current &&
					K_NP.current &&
					D_NDPI.current &&
					K_NDPI.current &&
					D_DIV.current &&
					K_NS.current &&
					D_VSO.current &&
					B_MIDDLE.current &&
					INDEX_TEN.current
				) {
					const [INDEX_EIGHTTop] = getTopChord([INDEX_EIGHT.current!]);
					const [INDEX_NINETop] = getTopChord([INDEX_NINE.current!]);
					const [K_NPTop] = getTopChord([K_NP.current!]);
					const [D_NDPITop] = getTopChord([D_NDPI.current!]);
					const [K_NDPITop] = getTopChord([K_NDPI.current!]);
					const [D_DIVTop] = getTopChord([D_DIV.current!]);
					const [K_NSTop] = getTopChord([K_NS.current!]);
					const [D_VSOTop] = getTopChord([D_VSO.current!]);
					const [B_MIDDLETop] = getTopChord([B_MIDDLE.current!]);
					const [INDEX_TENTop] = getTopChord([INDEX_TEN.current!]);

					const TOP = containerTop - 15;
					if (INDEX_TENTop < TOP) {
						setObject([
							{
								name: "К разделу",
								onClick: () => {
									INDEX_TEN.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
							{
								name: "К началу",
								onClick: () => {
									INDEX_EIGHT.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
						]);
					} else if (B_MIDDLETop < TOP) {
						setObject([
							{
								name: "К разделу",
								onClick: () => {
									B_MIDDLE.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
							{
								name: "К началу",
								onClick: () => {
									INDEX_EIGHT.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
						]);
					} else if (D_VSOTop < TOP) {
						setObject([
							{
								name: "К разделу",
								onClick: () => {
									D_VSO.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
							{
								name: "К началу",
								onClick: () => {
									INDEX_EIGHT.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
						]);
					} else if (K_NSTop < TOP) {
						setObject([
							{
								name: "К разделу",
								onClick: () => {
									K_NS.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
							{
								name: "К началу",
								onClick: () => {
									INDEX_EIGHT.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
						]);
					} else if (D_DIVTop < TOP) {
						setObject([
							{
								name: "К разделу",
								onClick: () => {
									D_DIV.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
							{
								name: "К началу",
								onClick: () => {
									INDEX_EIGHT.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
						]);
					} else if (K_NDPITop < TOP) {
						setObject([
							{
								name: "К разделу",
								onClick: () => {
									K_NDPI.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
							{
								name: "К началу",
								onClick: () => {
									INDEX_EIGHT.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
						]);
					} else if (D_NDPITop < TOP) {
						setObject([
							{
								name: "К разделу",
								onClick: () => {
									D_NDPI.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
							{
								name: "К началу",
								onClick: () => {
									INDEX_EIGHT.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
						]);
					} else if (K_NPTop < TOP) {
						setObject([
							{
								name: "К разделу",
								onClick: () => {
									K_NP.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
							{
								name: "К началу",
								onClick: () => {
									INDEX_EIGHT.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
						]);
					} else if (INDEX_NINETop < TOP) {
						setObject([
							{
								name: "К разделу",
								onClick: () => {
									INDEX_NINE.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
							{
								name: "К началу",
								onClick: () => {
									INDEX_EIGHT.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
						]);
					} else if (INDEX_EIGHTTop < TOP) {
						setObject([
							{
								name: "К разделу",
								onClick: () => {
									INDEX_EIGHT.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
							{
								name: "К началу",
								onClick: () => {
									INDEX_EIGHT.current?.scrollIntoView({ block: "start", behavior: "smooth" });
								},
							},
						]);
					} else {
						setObject([]);
					}
				}
			}
		}
		function wheel(e: WheelEvent & { wheelDeltaY: number }) {
			if (e.wheelDeltaY) {
				if (e.wheelDeltaY > 0) {
					setScrollTop(true);
				} else {
					setTimeout(() => {
						setScrollTop(false);
					}, 200);
				}
			}
		}

		const cont = containerRef.current;
		cont?.addEventListener("scroll", scroll);
		cont?.addEventListener("wheel", (e: any) => wheel(e as WheelEvent & { wheelDeltaY: number }));
		return () => {
			cont?.removeEventListener("scroll", scroll);
			cont?.removeEventListener("wheel", (e: any) => wheel(e as WheelEvent & { wheelDeltaY: number }));
		};
		//eslint-disable-next-line
	}, []);

	/**
	 * Функция для смены адреса объекта
	 */
	const onSelectAddress = (e: { [key: string]: any }) => {
		setLocation(e as any);
		if (!isFinal) {
			if (e.id === 0) {
				navigate(appLocation.pathname.replace(/address\/\d+$/, `result`), { replace: true });
			} else {
				if (/address\/\d+$/.test(appLocation.pathname)) {
					navigate(appLocation.pathname.replace(/\d+$/, e.id), { replace: true });
				} else {
					navigate(appLocation.pathname + "/address/" + e.id, { replace: true });
				}
			}
		} else {
			navigate(appLocation.pathname.replace(/result$/, `address/${e.id}`), { replace: true });
		}
	};
	return (
		<>
			<HeadPage
				subTitle={name ? name : ""}
				topTitle={"4.2 Формирование модели угроз"}
				title={"Расчет экономического ущерба"}>
				<div className={sass.header}>
					<InputTemp
						onSelect={onSelectAddress}
						currentValue={location}
						searchIcon
						value={isFinal ? "Суммарный результат по адресам" : location?.address}
						className={sass.input}
						type={"select"}
						options={locationOptions}
						field={["address"]}
						placeholder={"Выберите адрес"}
					/>
					{!(isFinal || (currentThread && currentThread.damageExists)) && (
						<Button
							disabled={!location.address || !issetFormulas}
							onClick={endCalculate}
							variant={BUTTON_VARIANTS.CONTAINED}>
							Завершить расчет
						</Button>
					)}
				</div>
				<Anchor classAnchor={sass[`anchor_${object.length && scrollTop}`]} fields={object} />
			</HeadPage>
			<div data-comment-false={true} className={isFinal ? sass.wrapper_grid : sass.wrapper} ref={containerRef}>
				{isFinal ? (
					loading ? null : finalList.length ? (
						finalList.map((_, index) => {
							const final = generateIndex(_.type);
							return (
								<div key={index} className={cn(sass.wrapper__item, sass.row)}>
									<span className={sass.row__title}>{names[_.type]}</span>
									<span className={sass.row__result}>
										{final.simple}
										{prettify(_.result!)}
										{final.endOf}
									</span>
								</div>
							);
						})
					) : (
						<div className={sass.emptyResult}>
							<p>Выберите адрес для заполнения данных и расчета показателей экономического ущерба</p>
						</div>
					)
				) : location.id !== 0 ? (
					state.map((_: any, index) => {
						return (
							<Row
								ref={_.ref}
								change={changeItemState}
								location={location.id}
								disable={!(location && location.address)}
								key={index}
								data={_}
								addIcon={_.addIcon}
								label={_.label ? _.name : undefined}
								removeField={_.removeField}
							/>
						);
					})
				) : (
					<div data-comment-false={true} className={sass.empty}>
						<div className={sass.empty_title}>Выберите адрес</div>
						<InputTemp
							style={{ ...(!location.id && { backgroundColor: "#F6FAFF" }) }}
							onSelect={onSelectAddress}
							searchIcon
							value={isFinal ? "Суммарный результат по адресам" : location?.address}
							className={sass.empty_input}
							type={"select"}
							options={locationOptions}
							field={["address"]}
							placeholder={"Выберите адрес"}
						/>
					</div>
				)}
			</div>
		</>
	);
};

export default Calculate;
